import { onSnapshot } from "firebase/firestore";

export function mapBoundedQueryDoc(docSnap) {
  return { id: docSnap.id, ...(docSnap.data?.() || {}) };
}

// Subscribe to a bounded (ordered + limited) query, falling back to an unbounded
// query if the bounded one errors. The fallback keeps a feature working before
// its supporting composite index is deployed; once the index exists the bounded
// query takes over automatically. Firestore detaches a listener that errors, so
// there is nothing to clean up for the failed bounded attempt.
export function subscribeBoundedQuery(boundedQuery, fallbackQuery, onChange, onError) {
  let current = null;
  let usedFallback = false;

  const start = (q, isFallback) => {
    const unsub = onSnapshot(
      q,
      (snap) => onChange(snap.docs.map(mapBoundedQueryDoc)),
      (err) => {
        if (!isFallback && !usedFallback) {
          usedFallback = true;
          console.warn(
            "Bounded query unavailable (likely a missing index); using unbounded fallback:",
            err?.message || err
          );
          start(fallbackQuery, true);
          return;
        }
        if (typeof onError === "function") onError(err);
      }
    );

    // Track the active subscription. Guard against the case where the bounded
    // query errors synchronously and hands off to the fallback before its own
    // onSnapshot() returns — that dead bounded listener must not overwrite the
    // active fallback subscription.
    if (isFallback || !usedFallback) {
      current = unsub;
    }
    return unsub;
  };

  start(boundedQuery, false);

  return () => {
    if (typeof current === "function") current();
  };
}

export default subscribeBoundedQuery;
